import React, { useState } from 'react';
import Icon from '../../components/AppIcon';

const ACCENT = '#60A5FA';
const GOLD = '#D6EF4E';

const PendingBadge = () => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
    style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.20)' }}>
    <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
    <span className="font-oranienbaum text-amber-400 text-xs tracking-widest uppercase">Pendiente de conexión con Supabase</span>
  </div>
);

const orderStates = [
  { label: 'Borrador', color: '#94A3B8', icon: 'FileEdit' },
  { label: 'Confirmado', color: ACCENT, icon: 'CheckCircle' },
  { label: 'Pend. Stock', color: '#FBBF24', icon: 'AlertTriangle' },
  { label: 'Preparación', color: '#FB923C', icon: 'Package' },
  { label: 'Preparado', color: '#4ADE80', icon: 'PackageCheck' },
  { label: 'En Reparto', color: '#38BDF8', icon: 'Truck' },
  { label: 'Entregado', color: '#34D399', icon: 'CheckCircle2' },
  { label: 'Facturado', color: GOLD, icon: 'FileText' },
  { label: 'Cancelado', color: '#F87171', icon: 'XCircle' },
  { label: 'Devuelto', color: '#A78BFA', icon: 'RotateCcw' },
  { label: 'Incidencia', color: '#DC2626', icon: 'AlertOctagon' },
];

const kpis = [
  { label: 'Pedidos Hoy', value: '—', icon: 'ShoppingCart', color: ACCENT },
  { label: 'Pedidos Pendientes', value: '—', icon: 'Clock', color: '#FBBF24' },
  { label: 'Preparados', value: '—', icon: 'PackageCheck', color: '#4ADE80' },
  { label: 'En Reparto', value: '—', icon: 'Truck', color: '#38BDF8' },
  { label: 'Entregados', value: '—', icon: 'CheckCircle', color: '#34D399' },
  { label: 'Con Incidencia', value: '—', icon: 'AlertOctagon', color: '#DC2626' },
  { label: 'Valor Pendiente', value: '—', icon: 'DollarSign', color: GOLD },
  { label: 'Ticket Medio', value: '—', icon: 'BarChart2', color: '#A78BFA' },
];

const columns = [
  'Nº Pedido', 'Fecha', 'Cliente', 'Comercial', 'Ruta', 'Importe',
  'Coste', 'Beneficio', 'Margen', 'Estado', 'F. Entrega',
  'Estado Facturación', 'Estado Cobro', 'Responsable'
];

const pageSizes = [25, 50, 100];

const DistPedidos = () => {
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('Todos');
  const [pageSize, setPageSize] = useState(25);
  const [visibleCols, setVisibleCols] = useState(new Set([
    'Nº Pedido', 'Fecha', 'Cliente', 'Comercial', 'Ruta',
    'Importe', 'Margen', 'Estado', 'F. Entrega', 'Estado Cobro'
  ]));
  const [showColSelector, setShowColSelector] = useState(false);

  const toggleCol = (col) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next?.has(col)) next?.delete(col); else next?.add(col);
      return next;
    });
  };

  const orderedVisible = columns?.filter(c => visibleCols?.has(c));

  return (
    <div className="min-h-screen px-4 py-6 md:px-8"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <nav className="flex items-center gap-2 text-xs font-oranienbaum text-white/30 mb-2">
            <span>🚚 Distribución</span>
            <Icon name="ChevronRight" size={12} />
            <span className="text-white/60">Pedidos</span>
          </nav>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
              <Icon name="ShoppingCart" size={18} style={{ color: ACCENT }} />
            </div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Pedidos Distribución</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-12">Centro operativo · Gestión de pedidos B2B</p>
        </div>
        <PendingBadge />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
        {kpis?.map((kpi, i) => (
          <div key={i} className="rounded-xl p-3 flex flex-col gap-1.5"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}25` }}>
              <Icon name={kpi?.icon} size={13} style={{ color: kpi?.color }} />
            </div>
            <p className="font-oranienbaum font-bold text-white text-lg">{kpi?.value}</p>
            <p className="font-oranienbaum text-white/45 text-[10px] leading-tight">{kpi?.label}</p>
          </div>
        ))}
      </div>

      {/* States Pipeline */}
      <div className="mb-6 p-4 rounded-2xl overflow-x-auto"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <h3 className="font-oranienbaum text-white/50 text-xs uppercase tracking-widest mb-3">Pipeline de Estados</h3>
        <div className="flex gap-2 min-w-max">
          {orderStates?.map((state, i) => (
            <button key={i}
              onClick={() => setStateFilter(stateFilter === state?.label ? 'Todos' : state?.label)}
              className="flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-xl transition-all min-w-[80px]"
              style={stateFilter === state?.label
                ? { background: `${state?.color}20`, border: `1px solid ${state?.color}40` }
                : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: `${state?.color}15`, border: `1px solid ${state?.color}25` }}>
                <Icon name={state?.icon} size={13} style={{ color: state?.color }} />
              </div>
              <span className="font-oranienbaum text-[10px] text-center leading-tight"
                style={{ color: stateFilter === state?.label ? state?.color : 'rgba(255,255,255,0.45)' }}>
                {state?.label}
              </span>
              <span className="font-oranienbaum font-bold text-xs"
                style={{ color: stateFilter === state?.label ? state?.color : 'rgba(255,255,255,0.30)' }}>
                0
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Stock reservation notice */}
      <div className="mb-4 p-3 rounded-xl flex items-start gap-3"
        style={{ background: 'rgba(96,165,250,0.05)', border: '1px solid rgba(96,165,250,0.15)' }}>
        <Icon name="Info" size={15} className="text-[#60A5FA] flex-shrink-0 mt-0.5" />
        <p className="font-oranienbaum text-[#60A5FA]/70 text-xs leading-relaxed">
          <strong className="text-[#60A5FA]">Flujo de stock:</strong> El stock se reserva al confirmar el pedido y se descuenta definitivamente al marcarlo como Entregado o Facturado, según configuración.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            placeholder="Buscar por número, cliente, comercial, ruta..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 placeholder-white/25 outline-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)' }}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <button onClick={() => setShowColSelector(!showColSelector)}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-oranienbaum text-white/60 hover:text-white/80 transition-colors"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)' }}>
              <Icon name="Columns" size={13} />
              Columnas
            </button>
            {showColSelector && (
              <div className="absolute right-0 top-full mt-1 w-52 rounded-xl shadow-2xl z-50 p-3 grid grid-cols-2 gap-1"
                style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.12)' }}>
                {columns?.map(col => (
                  <label key={col} className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-white/5 transition-colors">
                    <input type="checkbox" checked={visibleCols?.has(col)} onChange={() => toggleCol(col)}
                      className="w-3 h-3 rounded accent-[#60A5FA]" />
                    <span className="font-oranienbaum text-white/60 text-[10px] truncate">{col}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <select value={pageSize} onChange={e => setPageSize(Number(e?.target?.value))}
            className="px-3 py-2.5 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)' }}>
            {pageSizes?.map(s => <option key={s} value={s} style={{ background: '#1A1A1A' }}>{s} / pág</option>)}
          </select>
          <button className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-oranienbaum transition-colors"
            style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30`, color: ACCENT }}>
            <Icon name="Plus" size={13} />
            Crear Pedido
          </button>
          <button className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-oranienbaum text-white/60 hover:text-white/80 transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)' }}>
            <Icon name="Download" size={13} />
            Exportar
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <th className="px-3 py-3 text-left">
                  <input type="checkbox" className="w-3.5 h-3.5 rounded accent-[#60A5FA]" />
                </th>
                {orderedVisible?.map(col => (
                  <th key={col} className="px-3 py-3 text-left">
                    <button className="flex items-center gap-1 font-oranienbaum text-white/50 text-xs hover:text-white/80 transition-colors">
                      {col}
                      <Icon name="ChevronsUpDown" size={10} className="text-white/25" />
                    </button>
                  </th>
                ))}
                <th className="px-3 py-3 text-left">
                  <span className="font-oranienbaum text-white/50 text-xs">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={orderedVisible?.length + 2} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <Icon name="ShoppingCart" size={22} className="text-white/20" />
                    </div>
                    <p className="font-oranienbaum text-white/30 text-sm">Sin pedidos registrados</p>
                    <p className="font-oranienbaum text-white/20 text-xs">Pendiente de conexión con Supabase</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="font-oranienbaum text-white/35 text-xs">0 pedidos · Página 1 de 1</p>
          <div className="flex items-center gap-1">
            {['ChevronLeft', 'ChevronRight']?.map((icon, i) => (
              <button key={i} className="w-7 h-7 rounded-lg flex items-center justify-center text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors">
                <Icon name={icon} size={14} />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 p-4 rounded-2xl"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <h3 className="font-oranienbaum text-white/50 text-xs uppercase tracking-widest mb-3">Acciones sobre Selección</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Confirmar', icon: 'CheckCircle' },
            { label: 'Reservar Stock', icon: 'Lock' },
            { label: 'Preparar', icon: 'Package' },
            { label: 'Hoja Preparación', icon: 'Printer' },
            { label: 'Asignar Ruta', icon: 'MapPin' },
            { label: 'Marcar En Reparto', icon: 'Truck' },
            { label: 'Marcar Entregado', icon: 'CheckCircle2' },
            { label: 'Facturar', icon: 'FileText' },
            { label: 'Cancelar', icon: 'XCircle' },
            { label: 'Registrar Devolución', icon: 'RotateCcw' },
            { label: 'Registrar Incidencia', icon: 'AlertOctagon' },
          ]?.map((action, i) => (
            <button key={i}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum text-white/50 hover:text-white/80 transition-colors"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Icon name={action?.icon} size={12} />
              {action?.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DistPedidos;
